/**
 * AutoPilotsLanding — the AP card grid.
 *
 * Two tabs:
 *   Aktiv — favorited AP instances (star-pinned). Notifications + preview stats
 *            driven by live queries. Konfiguration AP always shown first.
 *   Alle  — all available AP templates as interactive cards with star toggle.
 *
 * Favorites persisted in localStorage['qms.aps.favorites'] as JSON string[].
 * Default favorites = ['configuration', 'supplier', 'customer-conformity', 'substanz-radar'].
 */

import { useTranslation, type TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { IconStar } from "@tabler/icons-react"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import { useCallback, useEffect, useState } from "react"
import { api } from "../../api/client"
import { useStore } from "../../store/useStore"
import { APCard } from "./APCard"
import type { ApType, AutoPilotTemplate } from "./registry"
import { ACTIVE_APS, ALL_TEMPLATES } from "./registry"

// ── Favorites helpers ─────────────────────────────────────────────────────────

const FAVORITES_KEY = "qms.aps.favorites"
const DEFAULT_FAVORITES = [
  "configuration",
  "supplier",
  "customer-conformity",
  "substanz-radar",
]

function loadFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed as string[]
    }
  } catch {
    /* ignore */
  }
  return [...DEFAULT_FAVORITES]
}

function saveFavorites(ids: string[]) {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids))
  } catch {
    /* ignore */
  }
}

// ── Template card with star toggle (Alle tab) ─────────────────────────────────

const AP_TYPE_LABEL_KEYS_LANDING: Record<ApType, TranslationKeys> = {
  configurator: "QMS/AP_TYPE/CONFIGURATOR",
  importer: "QMS/AP_TYPE/IMPORTER",
  exporter: "QMS/AP_TYPE/EXPORTER",
  monitor: "QMS/AP_TYPE/MONITOR",
}

function APTemplateCardWithStar({
  template,
  isFavorite,
  onToggleFavorite,
  onSelect,
}: {
  template: AutoPilotTemplate
  isFavorite: boolean
  onToggleFavorite: () => void
  onSelect: (apId: string) => void
}) {
  const { translate } = useTranslation()
  const Icon = template.icon
  const typeLabel = translate(AP_TYPE_LABEL_KEYS_LANDING[template.apType])

  return (
    <div className="group relative aspect-3/4 w-full overflow-hidden rounded-2xl border border-grey4 bg-grey1">
      {/* Faded background icon */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <Icon size={130} className="text-white opacity-[0.04]" />
      </div>

      {/* Star toggle */}
      <button
        onClick={onToggleFavorite}
        className={clsx(
          "absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border transition-colors",
          isFavorite
            ? "border-yellow1/50 bg-yellow1/15 text-yellow1"
            : "border-grey4 bg-grey3/40 text-grey5 hover:border-grey5 hover:text-white",
        )}
        title={isFavorite ? translate("QMS/LANDING/REMOVE_FROM_ACTIVE") : translate("QMS/LANDING/ADD_TO_ACTIVE")}
      >
        <IconStar size={14} fill={isFavorite ? "currentColor" : "none"} />
      </button>

      {/* Clickable content */}
      <button
        onClick={() => onSelect(template.id)}
        className="absolute inset-0 flex flex-col p-6 text-left transition-all duration-300 ease-in-out group-hover:bg-grey2/40"
      >
        <div className="flex-1" />
        <div className="space-y-3">
          <p className="text-2xl font-semibold leading-snug text-white">
            {translate(template.name as TranslationKeys)}
          </p>
          <p className="line-clamp-2 h-13 text-base font-light leading-relaxed text-grey5">
            {translate(template.description as TranslationKeys)}
          </p>
        </div>
        <div className="mt-6 border-t border-grey4/60 pt-3">
          <span className="text-xs font-medium uppercase tracking-[0.12em] text-grey5/50">
            {typeLabel}
          </span>
        </div>
      </button>
    </div>
  )
}

function getApLastOpenedAt(apId: string): number | null {
  try {
    const v = localStorage.getItem(`qms.ap.lastOpenedAt.${apId}`)
    return v ? Number(v) : null
  } catch {
    return null
  }
}

// SQLite stores datetimes as naive UTC strings (no TZ marker). Parse as UTC
// explicitly so comparisons with Date.now() are correct in non-UTC locales.
function parseUploadedAt(s: string): number {
  const iso = s.includes("T") ? s : s.replace(" ", "T")
  return new Date(
    iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z",
  ).getTime()
}

type LandingTab = "aktiv" | "alle"

interface Props {
  onSelect: (apId: string) => void
}

export function AutoPilotsLanding({ onSelect }: Props) {
  const { translate } = useTranslation()
  const [tab, setTab] = useState<LandingTab>("aktiv")
  const [favorites, setFavorites] = useState<string[]>(() => loadFavorites())

  const {
    dbConfigured,
    scopes,
    totalRecordCount,
    scopeRecordCounts,
    exporterThreshold,
    clearSelection,
  } = useStore()

  // Reset selection on landing so stale importer selections don't persist.
  useEffect(() => {
    clearSelection()
  }, [clearSelection])

  const toggleFavorite = useCallback((apId: string) => {
    setFavorites((prev) => {
      const next = prev.includes(apId)
        ? prev.filter((id) => id !== apId)
        : [...prev, apId]
      saveFavorites(next)
      return next
    })
  }, [])

  // v4: poll pending-document counts so Lieferanten card stays current.
  const { data: pendingDocs } = useQuery({
    queryKey: ["pendingDocuments", "supplier"],
    queryFn: () => api.pendingDocumentsList("supplier"),
    enabled: dbConfigured === true,
    refetchInterval: 15_000,
  })
  const pendingDocCountByAp = { supplier: pendingDocs?.length ?? 0 }

  const supplierLastOpenedAt = getApLastOpenedAt("supplier")
  const newDocCountByAp = {
    supplier:
      pendingDocs?.filter(
        (d) =>
          d.source !== "upload" &&
          (!supplierLastOpenedAt ||
            parseUploadedAt(d.uploaded_at) > supplierLastOpenedAt),
      ).length ?? 0,
  }

  // v6: pre-fetch open monitor findings so the Substanz Radar card carries
  // its notification badge before the user opens the AP for the first time.
  const { data: substanzFindings } = useQuery({
    queryKey: ["monitor-findings-open", "substanz-radar"],
    queryFn: () => api.monitorListFindings("substanz-radar", "open"),
    enabled: dbConfigured === true,
    refetchInterval: 30_000,
  })

  // v4.1: also pre-fetch lieferanten-tracking findings for its card badge.
  const { data: ltFindings } = useQuery({
    queryKey: ["monitor-findings-open", "lieferanten-tracking"],
    queryFn: () => api.monitorListFindings("lieferanten-tracking", "open"),
    enabled: dbConfigured === true,
    refetchInterval: 30_000,
  })

  const monitorOpenCountByAp = {
    "substanz-radar": substanzFindings?.length ?? 0,
    "lieferanten-tracking": ltFindings?.length ?? 0,
  }

  // v6: customer-conformity notification — count fresh customers whose
  // downstream chain was last touched after the AP was last opened.
  const { data: freshness } = useQuery({
    queryKey: ["freshness", exporterThreshold],
    queryFn: () => api.freshnessPartition(exporterThreshold),
    enabled: dbConfigured === true,
    refetchInterval: 15_000,
  })
  const customerLastOpenedAt = getApLastOpenedAt("customer-conformity")
  const customerNewFreshCount = (() => {
    if (!freshness) return 0
    const fresh = freshness.fresh_record_ids ?? []
    const updates = freshness.latest_chain_update_per_record ?? {}
    if (fresh.length === 0) return 0
    if (!customerLastOpenedAt) {
      // First-ever visit: any fresh customer counts as "new" so the badge
      // surfaces only after data started moving (initial state has no fresh).
      return fresh.length
    }
    return fresh.filter((id) => {
      const ts = updates[id]
      if (!ts) return false
      const updatedAt = parseUploadedAt(ts)
      return updatedAt > customerLastOpenedAt
    }).length
  })()

  const customerFreshCount = freshness?.fresh_record_ids?.length ?? 0

  // "In Bearbeitung" on the supplier tile = distinct supplier records that
  // have at least one staged pending document. Derived from pendingDocs
  // because the store's selectedRecordIds is cleared on landing mount.
  const supplierInProgressIds = new Set<string>()
  for (const d of pendingDocs ?? []) {
    if (d.target_record_id) supplierInProgressIds.add(d.target_record_id)
  }

  const storeData = {
    dbConfigured,
    scopes,
    totalRecordCount,
    scopeRecordCounts,
    selectedSupplierRecordIds: supplierInProgressIds,
    pendingDocCountByAp,
    newDocCountByAp,
    monitorOpenCountByAp,
    customerNewFreshCount,
    customerFreshCount,
  }

  // Aktiv tab: configuration AP always first; the rest follow registry
  // order. Static layout — does not reshuffle based on notification count.
  const favSet = new Set(favorites)
  const aktivAps = [
    ...ACTIVE_APS.filter((ap) => ap.id === "configuration"),
    ...ACTIVE_APS.filter(
      (ap) => ap.id !== "configuration" && favSet.has(ap.id),
    ),
  ]

  return (
    <div className="h-full overflow-auto bg-grey2 p-8">
      {/* Page header */}
      <h1 className="text-2xl font-semibold text-white">
        {translate("QMS/LANDING/TITLE")}
      </h1>

      {/* Tab pills */}
      <div className="mt-4 flex items-center gap-1">
        <TabPill active={tab === "aktiv"} onClick={() => setTab("aktiv")}>
          {translate("QMS/LANDING/TAB_ACTIVE")}
        </TabPill>
        <TabPill active={tab === "alle"} onClick={() => setTab("alle")}>
          {translate("QMS/LANDING/TAB_ALL")}
        </TabPill>
      </div>

      {/* Card grid */}
      <div className="mt-6">
        {tab === "aktiv" ? (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {aktivAps.map((ap) => (
              <APCard
                key={ap.id}
                ap={ap}
                storeData={storeData}
                onSelect={onSelect}
              />
            ))}
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-grey5">
              {translate("QMS/LANDING/ALL_DESCRIPTION")}
            </p>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {ALL_TEMPLATES.map((tpl) => (
                <APTemplateCardWithStar
                  key={tpl.id}
                  template={tpl}
                  isFavorite={tpl.id === "configuration" || favSet.has(tpl.id)}
                  onToggleFavorite={() =>
                    tpl.id !== "configuration" && toggleFavorite(tpl.id)
                  }
                  onSelect={onSelect}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function TabPill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "rounded-full px-4 py-1.5 text-sm transition-colors",
        active ? "bg-grey4 text-white" : "text-grey5 hover:text-white",
      )}
    >
      {children}
    </button>
  )
}
